import { IRequesterInfo } from '../../../../base/models'
import { ClientProxy } from '../../../../base/proxy/clientProxy'
import {
  IAppleDepAssignCustomerRequest,
  IAppleDepAssignCustomerResult,
  IAppleDepCustomer,
  IAppleDepDevice,
  IAppleDepDeviceFilter,
  IAppleDepEnvironment,
  IAppleDepExcelImportRequest,
  IAppleDepExcelImportResult,
  IAppleDepSendRequest,
  IAppleDepSendResult,
  IAppleDepTransaction,
  IAppleDepTransactionFilter,
} from '../models/models'

/** Bağlı olunan Apple ortamı (canlı/test) ve bayinin Reseller ID durumu. */
export class GetAppleDepEnvironmentApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/GetEnvironmentInfo', requesterInfo })
  }

  public async getEnvironment(): Promise<IAppleDepEnvironment> {
    return await this.getAsync<IAppleDepEnvironment>()
  }
}

/** SAP'ten bayiye satılan Apple cihazlarını çeker ve veritabanıyla birleştirir. */
export class GetAppleDepDevicesApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/GetDevices', requesterInfo })
  }

  public async getDevices(request: IAppleDepDeviceFilter): Promise<IAppleDepDevice[]> {
    return await this.postAsync<IAppleDepDevice[]>(request)
  }
}

/** Seçili cihazlara müşteri (Organization ID) ve bayi sipariş bilgisini atar. */
export class AssignAppleDepCustomerApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/AssignCustomer', requesterInfo })
  }

  public async assignCustomer(request: IAppleDepAssignCustomerRequest): Promise<IAppleDepAssignCustomerResult> {
    return await this.postAsync<IAppleDepAssignCustomerResult>(request)
  }
}

/**
 * Apple'ın Distributor Enrollment Form şablonuyla toplu müşteri ataması.
 * Apple'a gönderim yapmaz. `ValidateOnly: true` ile önizleme alınır.
 */
export class ImportAppleDepExcelApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/ImportFromExcel', requesterInfo })
  }

  public async importExcel(request: IAppleDepExcelImportRequest): Promise<IAppleDepExcelImportResult> {
    return await this.postAsync<IAppleDepExcelImportResult>(request)
  }
}

/** Müşteri atamasını geri alır (yalnızca gönderilmemiş cihazlarda). */
export class ClearAppleDepCustomerApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/ClearCustomer', requesterInfo })
  }

  public async clearCustomer(deviceIds: number[]): Promise<any> {
    return await this.postAsync<any>(deviceIds)
  }
}

export class GetAppleDepCustomersApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/GetCustomers', requesterInfo })
  }

  public async getCustomers(): Promise<IAppleDepCustomer[]> {
    return await this.getAsync<IAppleDepCustomer[]>()
  }
}

export class SaveAppleDepCustomerApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/SaveCustomer', requesterInfo })
  }

  public async saveCustomer(request: IAppleDepCustomer): Promise<{ Id: number }> {
    return await this.postAsync<{ Id: number }>(request)
  }
}

/**
 * Bayinin kendi kurumunun Apple Business Manager bilgisi. Bayi kendi girer;
 * Index admin de aynı değeri düzeltebilir.
 */
export class SaveMyAppleDepOrganizationApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/SaveMyOrganization', requesterInfo })
  }

  public async saveMyOrganization(request: { OrganizationId: string; OrganizationName?: string }): Promise<any> {
    return await this.postAsync<any>(request)
  }
}

export class DeleteAppleDepCustomerApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/DeleteCustomer', requesterInfo })
  }

  public async deleteCustomer(id: number): Promise<boolean> {
    return await this.postAsync<boolean>({ Id: id })
  }
}

/**
 * Seçili cihazları Apple'a gönderir. Otomatik gönderim yoktur; bu çağrı
 * yalnızca kullanıcı butona bastığında yapılır.
 */
export class SendToAppleApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/SendToApple', requesterInfo })
  }

  public async sendToApple(request: IAppleDepSendRequest): Promise<IAppleDepSendResult> {
    return await this.postAsync<IAppleDepSendResult>(request)
  }
}

export class GetAppleDepTransactionsApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/GetTransactions', requesterInfo })
  }

  public async getTransactions(request: IAppleDepTransactionFilter): Promise<IAppleDepTransaction[]> {
    return await this.postAsync<IAppleDepTransaction[]>(request)
  }
}

export class GetAppleDepTransactionDetailApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/GetTransactionDetail', requesterInfo })
  }

  public async getTransactionDetail(id: number): Promise<IAppleDepTransaction> {
    return await this.postAsync<IAppleDepTransaction>({ Id: id })
  }
}

/** Apple'dan gönderim statüsünü anında sorgular (saatlik job'a ek olarak). */
export class CheckAppleDepTransactionStatusApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/CheckTransactionStatus', requesterInfo })
  }

  public async checkStatus(id: number): Promise<IAppleDepTransaction> {
    return await this.postAsync<IAppleDepTransaction>({ Id: id })
  }
}

/** Bir gönderimdeki hatalı cihazları ayrı bir gönderimle tekrar dener. */
export class RetryAppleDepFailedDevicesApi extends ClientProxy {
  constructor(requesterInfo: IRequesterInfo) {
    super({ url: 'AppleDep/RetryFailedDevices', requesterInfo })
  }

  public async retryFailed(id: number): Promise<IAppleDepSendResult> {
    return await this.postAsync<IAppleDepSendResult>({ Id: id })
  }
}
